import React, { useState, useEffect } from 'react';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import ProjectPoster from './ProjectPoster';
import ProjectDetailModal from './ProjectDetailModal';
import { colorForRevenueCategory } from '../data/showcaseProjects';
import InvestmentModal from './InvestmentModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ──────────────────────────────────────────────
// ProjectsShowcase
// Three-tier curated projects view: Now Funding → Community Greenlight → In Distribution.
// ──────────────────────────────────────────────

const fmt = (n) => `$${n.toLocaleString()}`;
const fmtK = (n) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n / 1000).toFixed(0)}K`);

export default function ProjectsShowcase() {
  const { user, setShowAuthFlow } = useDynamicContext();
  const [investProject, setInvestProject] = useState(null);
  const [votedFor, setVotedFor] = useState({}); // { projectId: 'up' | 'down' }
  const [openDetail, setOpenDetail] = useState(null); // { project, tier }
  const [showcase, setShowcase] = useState(null); // { nowFunding, communityVoting, inDistribution }
  const [loadError, setLoadError] = useState(null);

  // Fetch showcase data on mount
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/showcase`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) setShowcase(res.data);
        else throw new Error('Malformed response');
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load projects');
      });
    return () => { cancelled = true; };
  }, []);

  const handleInvestClick = (project) => {
    if (!user) {
      setShowAuthFlow(true);
      return;
    }
    setInvestProject(project);
  };

  const handleVote = (projectId, direction) => {
    setVotedFor((prev) => ({ ...prev, [projectId]: direction }));
  };

  const openCard = (project, tier) => setOpenDetail({ project, tier });
  const closeDetail = () => setOpenDetail(null);

  // Loading state
  if (!showcase && !loadError) {
    return (
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '5rem 1.5rem', textAlign: 'center', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.875rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        Loading projects…
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div style={{ maxWidth: '640px', margin: '5rem auto', padding: '2rem 1.5rem', textAlign: 'center' }}>
        <div style={{ color: '#F87171', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
          Couldn't load projects
        </div>
        <div style={{ color: '#94A3B8', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
          {loadError}. This is usually a temporary backend hiccup.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '10px',
            color: '#67E8F9',
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const { nowFunding, communityVoting, inDistribution } = showcase;

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '2rem 1.5rem 6rem' }}>
      <style>{`
        .showcase-section-title {
          font-family: 'Outfit', system-ui, sans-serif;
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: -0.03em;
          color: #F1F5F9;
          margin: 0 0 0.25rem;
        }
        .showcase-section-eyebrow {
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #06B6D4;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
        }
        .showcase-section-subtitle {
          font-size: 0.95rem;
          color: #94A3B8;
          margin: 0 0 2rem;
          max-width: 640px;
          line-height: 1.55;
        }

        .hero-card {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 2rem;
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.6) 100%);
          border: 1px solid rgba(6, 182, 212, 0.15);
          border-radius: 20px;
          padding: 1.75rem;
          transition: transform 400ms cubic-bezier(0.16, 1, 0.3, 1), border-color 400ms ease, box-shadow 400ms ease;
        }
        .hero-card:hover {
          transform: translateY(-4px);
          border-color: rgba(6, 182, 212, 0.35);
          box-shadow: 0 12px 50px rgba(6, 182, 212, 0.12);
        }
        .hero-card .poster-wrap {
          aspect-ratio: 2/3;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
        }

        .vote-card {
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(15, 23, 42, 0.5) 100%);
          border: 1px solid rgba(255, 255, 255, 0.04);
          border-radius: 16px;
          padding: 1.25rem;
          transition: transform 350ms cubic-bezier(0.16, 1, 0.3, 1), border-color 350ms ease;
        }
        .vote-card:hover {
          transform: translateY(-3px);
          border-color: rgba(132, 204, 22, 0.25);
        }
        .vote-card .poster-wrap {
          aspect-ratio: 2/3;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 1rem;
          box-shadow: 0 8px 22px rgba(0, 0, 0, 0.45);
        }

        .dist-row {
          display: grid;
          grid-template-columns: 140px 1fr;
          gap: 1.5rem;
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.4) 0%, rgba(15, 23, 42, 0.4) 100%);
          border: 1px solid rgba(255, 255, 255, 0.04);
          border-radius: 16px;
          padding: 1.25rem;
          transition: border-color 300ms ease;
        }
        .dist-row:hover {
          border-color: rgba(168, 85, 247, 0.2);
        }
        .dist-row .poster-wrap {
          aspect-ratio: 2/3;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
        }

        .invest-btn {
          padding: 0.85rem 1.75rem;
          background: linear-gradient(135deg, #06B6D4 0%, #84CC16 100%);
          color: #0F172A;
          border: none;
          border-radius: 10px;
          font-family: 'Instrument Sans', system-ui, sans-serif;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          transition: transform 250ms ease, box-shadow 250ms ease;
        }
        .invest-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(6, 182, 212, 0.3);
        }

        .vote-btn {
          flex: 1;
          padding: 0.65rem;
          background: rgba(132, 204, 22, 0.08);
          color: #84CC16;
          border: 1px solid rgba(132, 204, 22, 0.2);
          border-radius: 10px;
          font-family: 'Instrument Sans', system-ui, sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 200ms ease;
        }
        .vote-btn:hover {
          background: rgba(132, 204, 22, 0.16);
          border-color: rgba(132, 204, 22, 0.4);
        }
        .vote-btn.voted {
          background: rgba(132, 204, 22, 0.25);
          border-color: rgba(132, 204, 22, 0.5);
          color: #A3E635;
        }
        .vote-btn-down {
          color: #94A3B8;
          background: rgba(148, 163, 184, 0.06);
          border-color: rgba(148, 163, 184, 0.15);
        }
        .vote-btn-down:hover {
          background: rgba(148, 163, 184, 0.12);
          border-color: rgba(148, 163, 184, 0.3);
        }
        .vote-btn-down.voted {
          background: rgba(148, 163, 184, 0.2);
          color: #CBD5E1;
        }

        @media (max-width: 900px) {
          .hero-card { grid-template-columns: 1fr; }
          .hero-card .poster-wrap { max-width: 280px; margin: 0 auto; }
          .dist-row { grid-template-columns: 1fr; }
          .dist-row .poster-wrap { max-width: 140px; }
        }
      `}</style>

      {/* ──────────── TIER 1: NOW FUNDING ──────────── */}
      <section style={{ marginBottom: '5rem' }}>
        <div className="showcase-section-eyebrow">★ Now funding</div>
        <h2 className="showcase-section-title">Open campaigns</h2>
        <p className="showcase-section-subtitle">
          Curated, vetted, and live for investment. Back the next films from filmmakers we've personally backed in.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(540px, 1fr))', gap: '1.75rem' }}>
          {nowFunding.map((p) => (
            <HeroCard
              key={p.id}
              project={p}
              onInvest={() => handleInvestClick(p)}
              onOpen={() => openCard(p, 'funding')}
            />
          ))}
        </div>
      </section>

      {/* ──────────── TIER 2: COMMUNITY GREENLIGHT ──────────── */}
      <section style={{ marginBottom: '5rem' }}>
        <div className="showcase-section-eyebrow" style={{ color: '#84CC16' }}>↑ Community greenlight</div>
        <h2 className="showcase-section-title">Vote to advance</h2>
        <p className="showcase-section-subtitle">
          These pitches are gathering community signal. When a project crosses 1,000 votes,
          INDI moves it into compliance review and opens crowdfunding.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
          {communityVoting.map((p) => (
            <VoteCard
              key={p.id}
              project={p}
              voted={votedFor[p.id]}
              onVote={(dir) => handleVote(p.id, dir)}
              onOpen={() => openCard(p, 'voting')}
            />
          ))}
        </div>
      </section>

      {/* ──────────── TIER 3: IN DISTRIBUTION ──────────── */}
      <section>
        <div className="showcase-section-eyebrow" style={{ color: '#A855F7' }}>● Released</div>
        <h2 className="showcase-section-title">Funded & in distribution</h2>
        <p className="showcase-section-subtitle">
          Films previously funded on INDI, now generating revenue across theatrical, streaming, and international windows.
          All revenue figures verified through INDI Distribution LLC.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {inDistribution.map((p) => (
            <DistributionRow
              key={p.id}
              project={p}
              onOpen={() => openCard(p, 'distribution')}
            />
          ))}
        </div>
      </section>

      {/* Detail modal */}
      <ProjectDetailModal
        project={openDetail?.project}
        tier={openDetail?.tier}
        onClose={closeDetail}
        currentVote={openDetail ? votedFor[openDetail.project.id] : undefined}
        onInvest={() => {
          if (!openDetail) return;
          handleInvestClick(openDetail.project);
          closeDetail();
        }}
        onVote={(dir) => {
          if (!openDetail) return;
          handleVote(openDetail.project.id, dir);
        }}
      />

      {/* Investment modal */}
      {investProject && user && (
        <InvestmentModal
          project={{
            title: investProject.title,
            _id: investProject.id,
            id: investProject.id,
          }}
          onClose={() => setInvestProject(null)}
          onSuccess={() => {
            alert(`Investment in "${investProject.title}" successful! 🎉`);
            setInvestProject(null);
          }}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// Hero card — Tier 1
// ──────────────────────────────────────────────
function HeroCard({ project, onInvest, onOpen }) {
  const { funding } = project;
  const pct = Math.min(100, Math.round((funding.raised / funding.goal) * 100));

  return (
    <article className="hero-card" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="poster-wrap">
        <ProjectPoster project={project} variant="hero" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.7rem',
            color: '#06B6D4',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            marginBottom: '0.4rem',
          }}>
            {project.genre}
          </div>
          <h3 style={{
            fontFamily: "'Outfit', system-ui, sans-serif",
            fontSize: '1.625rem',
            fontWeight: '700',
            color: '#F1F5F9',
            letterSpacing: '-0.02em',
            margin: '0 0 0.5rem',
            lineHeight: '1.15',
          }}>
            {project.title}
          </h3>
          <p style={{
            fontStyle: 'italic',
            color: '#94A3B8',
            fontSize: '0.95rem',
            lineHeight: '1.5',
            margin: '0 0 0.75rem',
          }}>
            "{project.tagline}"
          </p>
          <p style={{
            color: '#B0B8C8',
            fontSize: '0.875rem',
            lineHeight: '1.6',
            margin: 0,
          }}>
            {project.logline}
          </p>
          <div style={{
            marginTop: '0.6rem',
            fontSize: '0.8125rem',
            color: '#64748B',
          }}>
            Directed by <span style={{ color: '#CBD5E1', fontWeight: '600' }}>{project.director}</span>
            {project.creditNote && <span> · {project.creditNote}</span>}
          </div>
        </div>

        {/* Progress block */}
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
            <span style={{
              fontFamily: "'Outfit', sans-serif",
              fontSize: '1.375rem',
              fontWeight: '700',
              color: '#84CC16',
            }}>
              {fmt(funding.raised)}
            </span>
            <span style={{ color: '#64748B', fontSize: '0.8125rem' }}>
              of {fmt(funding.goal)} goal
            </span>
          </div>
          <div style={{
            height: '8px',
            background: 'rgba(6, 182, 212, 0.08)',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${pct}%`,
              background: 'linear-gradient(90deg, #06B6D4, #84CC16)',
              borderRadius: '4px',
              transition: 'width 600ms cubic-bezier(0.16, 1, 0.3, 1)',
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: '#94A3B8',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            <span>{pct}% funded</span>
            <span>{funding.investors} investors</span>
            <span>{funding.daysLeft} days left</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', alignItems: 'center' }}>
          <button
            className="invest-btn"
            onClick={(e) => { e.stopPropagation(); onInvest(); }}
          >
            Invest from ${funding.minInvestment}
          </button>
          <span style={{ color: '#64748B', fontSize: '0.8125rem' }}>
            Reg CF · No wallet required
          </span>
        </div>
      </div>
    </article>
  );
}

// ──────────────────────────────────────────────
// Vote card — Tier 2
// ──────────────────────────────────────────────
function VoteCard({ project, voted, onVote, onOpen }) {
  const { voting } = project;
  const adjustedCurrent = voting.current + (voted === 'up' ? 1 : 0);
  const pct = Math.min(100, Math.round((adjustedCurrent / voting.threshold) * 100));

  return (
    <article className="vote-card" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="poster-wrap">
        <ProjectPoster project={project} variant="medium" />
      </div>

      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '0.65rem',
        color: '#84CC16',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        marginBottom: '0.35rem',
      }}>
        {project.genre}
      </div>
      <h3 style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#F1F5F9',
        letterSpacing: '-0.02em',
        margin: '0 0 0.4rem',
      }}>
        {project.title}
      </h3>
      <p style={{
        fontStyle: 'italic',
        color: '#94A3B8',
        fontSize: '0.875rem',
        lineHeight: '1.45',
        margin: '0 0 0.5rem',
      }}>
        "{project.tagline}"
      </p>
      <p style={{
        fontSize: '0.8125rem',
        color: '#8892A4',
        lineHeight: '1.5',
        margin: '0 0 0.75rem',
      }}>
        {project.logline}
      </p>
      <div style={{ fontSize: '0.75rem', color: '#64748B', marginBottom: '0.85rem' }}>
        Directed by <span style={{ color: '#CBD5E1', fontWeight: '600' }}>{project.director}</span>
      </div>

      {/* Greenlight progress */}
      <div style={{ marginBottom: '0.85rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: '#94A3B8',
          fontFamily: "'JetBrains Mono', monospace",
          marginBottom: '0.3rem',
        }}>
          <span>{adjustedCurrent.toLocaleString()} of {voting.threshold.toLocaleString()} votes</span>
          <span style={{ color: '#84CC16' }}>{pct}%</span>
        </div>
        <div style={{
          height: '5px',
          background: 'rgba(132, 204, 22, 0.08)',
          borderRadius: '3px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(90deg, #84CC16, #A3E635)',
            borderRadius: '3px',
            transition: 'width 500ms ease',
          }} />
        </div>
        <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.3rem' }}>
          {pct < 100
            ? `${(voting.threshold - adjustedCurrent).toLocaleString()} votes to greenlight`
            : 'Greenlit — entering compliance review'}
        </div>
      </div>

      {/* Vote buttons */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          className={`vote-btn ${voted === 'up' ? 'voted' : ''}`}
          onClick={(e) => { e.stopPropagation(); onVote('up'); }}
          disabled={voted === 'up'}
        >
          ▲ Greenlight
        </button>
        <button
          className={`vote-btn vote-btn-down ${voted === 'down' ? 'voted' : ''}`}
          onClick={(e) => { e.stopPropagation(); onVote('down'); }}
          disabled={voted === 'down'}
        >
          ▼ Pass
        </button>
      </div>
    </article>
  );
}

// ──────────────────────────────────────────────
// Distribution row — Tier 3
// ──────────────────────────────────────────────
function DistributionRow({ project, onOpen }) {
  const { release } = project;
  const roiMultiple = (release.revenueTotal / release.totalRaised).toFixed(2);

  return (
    <article className="dist-row" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <div className="poster-wrap">
        <ProjectPoster project={project} variant="compact" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {/* Header row */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.65rem',
              color: '#A855F7',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
            }}>
              {project.genre}
            </span>
            <span style={{
              fontSize: '0.7rem',
              padding: '0.15rem 0.6rem',
              background: 'rgba(168, 85, 247, 0.12)',
              border: '1px solid rgba(168, 85, 247, 0.25)',
              borderRadius: '4px',
              color: '#C4B5FD',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.05em',
            }}>
              {release.status}
            </span>
          </div>

          <h3 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#F1F5F9',
            letterSpacing: '-0.02em',
            margin: '0 0 0.3rem',
          }}>
            {project.title}
          </h3>
          <p style={{
            color: '#8892A4',
            fontSize: '0.8125rem',
            margin: 0,
            fontStyle: 'italic',
          }}>
            "{project.tagline}" · Directed by <span style={{ color: '#CBD5E1' }}>{project.director}</span>
          </p>
        </div>

        {/* Distribution channels */}
        <div>
          <div style={{
            fontSize: '0.7rem',
            color: '#64748B',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '0.4rem',
          }}>
            Distribution
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {release.channels.map((ch) => (
              <span key={ch} style={{
                padding: '0.3rem 0.75rem',
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.18)',
                borderRadius: '6px',
                fontSize: '0.75rem',
                color: '#67E8F9',
                fontFamily: "'Instrument Sans', sans-serif",
                fontWeight: '500',
              }}>
                {ch}
              </span>
            ))}
          </div>
        </div>

        {/* Financial summary + revenue breakdown */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '1.5rem',
          alignItems: 'start',
          paddingTop: '0.5rem',
          borderTop: '1px solid rgba(255, 255, 255, 0.04)',
        }}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                Raised
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.25rem', fontWeight: '700', color: '#22D3EE' }}>
                {fmtK(release.totalRaised)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                Revenue
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.25rem', fontWeight: '700', color: '#A3E635' }}>
                {fmtK(release.revenueTotal)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: '#64748B', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
                Multiple
              </div>
              <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.25rem', fontWeight: '700', color: '#F472B6' }}>
                {roiMultiple}x
              </div>
            </div>
          </div>

          <RevenueBreakdownBar breakdown={release.revenueBreakdown} total={release.revenueTotal} />
        </div>
      </div>
    </article>
  );
}

// ──────────────────────────────────────────────
// Stacked horizontal revenue breakdown bar
// ──────────────────────────────────────────────
function RevenueBreakdownBar({ breakdown, total }) {
  return (
    <div>
      <div style={{
        fontSize: '0.7rem',
        color: '#64748B',
        fontFamily: "'JetBrains Mono', monospace",
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        marginBottom: '0.4rem',
      }}>
        Revenue breakdown <span style={{ textTransform: 'none', letterSpacing: 0, opacity: 0.6 }}>· hover for amounts</span>
      </div>
      <div style={{
        display: 'flex',
        height: '14px',
        borderRadius: '7px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.03)',
        marginBottom: '0.65rem',
      }}>
        {breakdown.map((seg) => {
          const pct = (seg.amount / total) * 100;
          const color = colorForRevenueCategory(seg.label);
          return (
            <div
              key={seg.label}
              style={{
                width: `${pct}%`,
                background: color,
                transition: 'width 600ms cubic-bezier(0.16, 1, 0.3, 1)',
                cursor: 'help',
              }}
              title={`${seg.label}: ${fmt(seg.amount)} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.25rem' }}>
        {breakdown.map((seg) => {
          const pct = (seg.amount / total) * 100;
          const color = colorForRevenueCategory(seg.label);
          return (
            <div
              key={seg.label}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'help' }}
              title={`${seg.label}: ${fmt(seg.amount)} (${pct.toFixed(1)}%)`}
            >
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '2px',
                background: color,
                flexShrink: 0,
              }} />
              <span style={{ color: '#94A3B8', whiteSpace: 'nowrap' }}>{seg.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
